"use client";

import { Sparkles } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { placeholderPaths } from "@/lib/shared/constants";
import type { ProductForClient, ProductVariantForClient } from "@/lib/shared/types/interfaces";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HStack,
  Text,
  VStack,
} from "@/ui";
import { ComparisonImageFrame } from "./ComparisonImageFrame";
import { TryOnReferenceNote } from "./TryOnReferenceNote";
import { TryOnVariantSelector } from "./TryOnVariantSelector";
import { useVirtualTryOnJobs } from "./VirtualTryOnJobsProvider";

interface VirtualTryOnDialogProps {
  product: ProductForClient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialVariantId?: string;
}

export function VirtualTryOnDialog({
  product,
  open,
  onOpenChange,
  initialVariantId,
}: VirtualTryOnDialogProps) {
  const locale = useLocale();
  const t = useTranslations("virtual_try_on");
  const { submitJob } = useVirtualTryOnJobs();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariantForClient>(() => {
    const variant =
      product.variants.find((item) => item.variant.id === initialVariantId) ?? product.variants[0];
    if (!variant) throw new Error("Product must have at least one variant");
    return variant;
  });

  const photoUrl = useMemo(() => (photoFile ? URL.createObjectURL(photoFile) : null), [photoFile]);
  const productThumbSrc = useMemo(() => {
    const image = product.images?.[selectedVariant.imageIndex ?? 0] ?? product.images?.[0];
    return (image?.thumbnailSrc ?? image?.src)?.trim() || placeholderPaths.image;
  }, [product.images, selectedVariant.imageIndex]);

  useEffect(() => {
    if (!photoUrl) return;
    return () => URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);
  useEffect(() => {
    if (!open) setPhotoFile(null);
  }, [open]);

  const handleGenerate = () => {
    if (!photoFile) return;
    submitJob({
      photo: photoFile,
      productName: product.name,
      sku: product.sku,
      locale,
      variantId: selectedVariant.variant.id,
    });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl gap-0 overflow-y-auto p-0 sm:max-w-2xl">
        <DialogHeader className="space-y-1 border-b border-border/50 px-6 py-5">
          <DialogTitle className="font-product-title text-lg font-medium tracking-normal">
            {t("dialog_title")}
          </DialogTitle>
          <Text className="text-sm text-muted-foreground">{t("dialog_subtitle")}</Text>
        </DialogHeader>
        <VStack className="gap-6 px-6 py-5">
          <TryOnReferenceNote />
          <Text className="font-product-title text-base text-foreground">{product.name}</Text>
          <TryOnVariantSelector
            product={product}
            selectedVariant={selectedVariant}
            onVariantChange={setSelectedVariant}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(event) => {
              setPhotoFile(event.target.files?.[0] ?? null);
              event.target.value = "";
            }}
          />
          <HStack className="gap-4">
            <ComparisonImageFrame
              label={t("product_image_label")}
              src={productThumbSrc}
              alt={product.name}
            />
            <ComparisonImageFrame
              label={t("your_photo_label")}
              src={photoUrl}
              alt={t("your_photo_label")}
              emptyHint={t("choose_photo")}
              emptySubhint={t("upload_hint")}
              changeHint={t("change_photo")}
              onUploadClick={() => fileInputRef.current?.click()}
              uploadAriaLabel={photoFile ? t("change_photo") : t("choose_photo")}
            />
          </HStack>
          <Button
            type="button"
            disabled={!photoFile}
            className="w-full rounded-none py-5 text-[11px] font-medium uppercase tracking-[0.15em]"
            onClick={handleGenerate}
          >
            <HStack className="items-center justify-center gap-2">
              <Sparkles className="size-4" />
              {t("generate")}
            </HStack>
          </Button>
        </VStack>
      </DialogContent>
    </Dialog>
  );
}
